import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Pencil, PackagePlus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

const validTabs = ["ingredients", "products", "opname", "stock-ledger"];

export default function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(requestedTab) ? requestedTab : "products"
  );
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (validTabs.includes(requested)) setActiveTab(requested);
  }, [searchParams]);
  const changeInventoryTab = (value) => {
    if (!validTabs.includes(value)) return;
    setActiveTab(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [balances, setBalances] = useState([]);
  const [orderCart, setOrderCart] = useState([]);
  const [orderQty, setOrderQty] = useState({});
  const [transferCart, setTransferCart] = useState([]);
  const [transferQty, setTransferQty] = useState({});
  const navigate = useNavigate();
  const [inventoryError, setInventoryError] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [productOutletFilter, setProductOutletFilter] = useState("all");
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productSort, setProductSort] = useState({ key: "name", direction: "asc", type: "text" });
  const [ingredientSort, setIngredientSort] = useState({ key: "name", direction: "asc", type: "text" });
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [ingredientStatusFilter, setIngredientStatusFilter] = useState("all");
  const [ingredientLocationFilter, setIngredientLocationFilter] = useState("all");
  const [stockProduct, setStockProduct] = useState(null);
  const [stockOpen, setStockOpen] = useState(false);
  const [stockForm, setStockForm] = useState({ delta: "", reason: "" });
  const [stockSaving, setStockSaving] = useState(false);
  const [minStockProduct, setMinStockProduct] = useState(null);
  const [minStockOpen, setMinStockOpen] = useState(false);
  const [minStockValue, setMinStockValue] = useState("");
  const [minStockSaving, setMinStockSaving] = useState(false);

  const [ingredientStockItem, setIngredientStockItem] = useState(null);
  const [ingredientStockOpen, setIngredientStockOpen] = useState(false);
  const [ingredientStockForm, setIngredientStockForm] = useState({ warehouse_id: "", delta: "", reason: "" });
  const [ingredientStockSaving, setIngredientStockSaving] = useState(false);
  const [ingredientMinItem, setIngredientMinItem] = useState(null);
  const [ingredientMinOpen, setIngredientMinOpen] = useState(false);
  const [ingredientMinValue, setIngredientMinValue] = useState("");
  const [ingredientMinSaving, setIngredientMinSaving] = useState(false);
  const [ingredientLocationsItem, setIngredientLocationsItem] = useState(null);
  const [ingredientLocationsOpen, setIngredientLocationsOpen] = useState(false);

  function addToOrderCart(entity, item, locationType, locationId, locationName) {
    if (!locationType || !locationId) {
      toast.error("Lokasi tujuan item belum tersedia"); return;
    }
    const key = `${entity}:${item.id}:${locationType}:${locationId}`;
    const qty = Number(orderQty[`${entity}:${item.id}`] ?? "0");
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Qty order harus lebih dari nol"); return;
    }
    const price = Number(entity === "ingredient" ? item.cost : item.hpp) || 0;
    setOrderCart(old => {
      const found = old.find(x => x.key === key);
      if (found) return old.map(x => x.key === key
        ? {...x, qty: Number(x.qty) + qty, subtotal: (Number(x.qty) + qty) * x.unit_price} : x);
      return [...old, {key, entity, entity_id: item.id, name: item.name, qty,
        unit: item.unit || "pcs", unit_price: price, subtotal: qty * price,
        location_type: locationType, location_id: locationId, location_name: locationName || locationId}];
    });
    setOrderQty(old => ({...old, [`${entity}:${item.id}`]: "0"}));
    toast.success(`${item.name} ditambahkan — ${locationName || locationId}`);
  }

  function addToTransferCart(entity, item, locationType, locationId, locationName) {
    if (!locationType || !locationId) {
      toast.error("Lokasi asal barang belum tersedia");
      return;
    }

    const key = `${entity}:${item.id}:${locationType}:${locationId}`;
    const qtyKey = key;
    const qty = Number(transferQty[qtyKey] ?? "0");

    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Qty Transfer harus lebih dari nol");
      return;
    }

    setTransferCart(old => {
      if (old.length && old.some(x =>
        x.location_type !== locationType || x.location_id !== locationId
      )) {
        toast.error("Satu keranjang Transfer hanya boleh memiliki satu lokasi asal");
        return old;
      }

      const found = old.find(x => x.key === key);
      if (found) {
        return old.map(x => x.key === key
          ? {...x, qty: Number(x.qty) + qty}
          : x
        );
      }

      return [...old, {
        key,
        entity,
        entity_id: item.id,
        name: item.name,
        qty,
        unit: item.unit || "pcs",
        location_type: locationType,
        location_id: locationId,
        location_name: locationName || locationId
      }];
    });

    setTransferQty(old => ({...old, [qtyKey]: "0"}));
  }

  const orderTotal = orderCart.reduce((sum, x) => sum + (Number(x.subtotal) || 0), 0);

  function sendOrderToPurchasing() {
    const items = orderCart.filter(x => Number(x.qty) > 0);
    if (!items.length) { toast.error("Daftar pesanan kosong atau qty tidak valid"); return; }
    navigate("/purchasing", {state: {inventoryOrderItems: items.map(({key, ...x}) => ({
      ...x, qty: Number(x.qty), subtotal: Number(x.qty) * Number(x.unit_price)
    }))}});
  }

  const toggleProductSort = (key, type = "text") => setProductSort(current => ({
    key, type,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc"
  }));

  const toggleIngredientSort = (key, type = "text") => setIngredientSort(current => ({
    key, type,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc"
  }));

  function openStock(product) {
    setStockProduct(product);
    setStockForm({ delta: "", reason: "" });
    setStockOpen(true);
  }

  function openMinStock(product) {
    setMinStockProduct(product);
    setMinStockValue(String(product.min_stock ?? 0));
    setMinStockOpen(true);
  }

  async function saveMinStock(e) {
    e.preventDefault();
    const value = Number(minStockValue);
    if (minStockValue.trim() === "" || !Number.isFinite(value) || value < 0) {
      toast.error("Stok minimum harus berupa angka nol atau lebih");
      return;
    }
    setMinStockSaving(true);
    try {
      await api.put(`/products/${minStockProduct.id}`, { min_stock: value });
      toast.success("Stok minimum berhasil diperbarui");
      setMinStockOpen(false);
      const [productsRes, balancesRes] = await Promise.all([
        api.get("/products"),
        api.get("/inventory/location-balances"),
      ]);
      setProducts(productsRes.data || []);
      setBalances(balancesRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui stok minimum");
    } finally {
      setMinStockSaving(false);
    }
  }

  function openIngredientStock(ingredient) {
    setIngredientStockItem(ingredient);
    setIngredientStockForm({
      location_type: "warehouse",
      location_id: centralWarehouse?.id || activeCentralWarehouses[0]?.id || "",
      delta: "",
      reason: "",
    });
    setIngredientStockOpen(true);
  }

  function openIngredientMin(ingredient) {
    setIngredientMinItem(ingredient);
    setIngredientMinValue(String(ingredient.min_stock ?? 5));
    setIngredientStockItem(ingredient);
    setIngredientStockForm({
      location_type: "warehouse",
      location_id: centralWarehouse?.id || activeCentralWarehouses[0]?.id || "",
      delta: "",
      reason: "",
    });
    setIngredientMinOpen(true);
  }

  async function refreshInventory() {
    const [productsRes, ingredientsRes, balancesRes] = await Promise.all([
      api.get("/products"),
      api.get("/ingredients"),
      api.get("/inventory/location-balances"),
    ]);
    setProducts(productsRes.data || []);
    setIngredients(ingredientsRes.data || []);
    setBalances(balancesRes.data || []);
  }

  async function saveIngredientMin(e) {
    e.preventDefault();
    const value = Number(ingredientMinValue);
    if (!ingredientMinValue.trim() || !Number.isFinite(value) || value < 0) {
      toast.error("Stok minimum harus berupa angka nol atau lebih");
      return;
    }
    setIngredientMinSaving(true);
    try {
      await api.put(`/ingredients/${ingredientMinItem.id}`, { min_stock: value });
      await refreshInventory();
      toast.success("Stok minimum bahan baku berhasil diperbarui");
      setIngredientMinOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui stok minimum bahan baku");
    } finally {
      setIngredientMinSaving(false);
    }
  }

  async function saveIngredientStock(e) {
    e.preventDefault();
    const delta = Number(ingredientStockForm.delta);
    if (!ingredientStockForm.location_id) {
      toast.error("Pilih lokasi stok terlebih dahulu");
      return;
    }
    if (!ingredientStockForm.delta.trim() || !Number.isFinite(delta) || delta === 0) {
      toast.error("Masukkan perubahan stok selain nol");
      return;
    }
    if (!ingredientStockForm.reason.trim()) {
      toast.error("Alasan penyesuaian stok wajib diisi");
      return;
    }
    setIngredientStockSaving(true);
    try {
      const { data } = await api.post("/ingredients/stock-adjust", {
        ingredient_id: ingredientStockItem.id,
        location_type: ingredientStockForm.location_type,
        location_id: ingredientStockForm.location_id,
        delta,
        reason: ingredientStockForm.reason.trim(),
      });
      await refreshInventory();
      toast.success(`Penyesuaian berhasil. Stok lokasi: ${Number(data.location_stock).toFixed(2)}; total semua lokasi: ${Number(data.total_stock).toFixed(2)} ${ingredientStockItem.unit || ""}`);
      setIngredientStockOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyesuaikan stok bahan baku");
    } finally {
      setIngredientStockSaving(false);
    }
  }

  async function saveStock(e) {
    e.preventDefault();
    const delta = Number(stockForm.delta);
    if (!stockForm.delta.trim() || !Number.isFinite(delta) || delta === 0) {
      toast.error("Masukkan perubahan stok selain nol");
      return;
    }
    if (!stockForm.reason.trim()) {
      toast.error("Alasan penyesuaian stok wajib diisi");
      return;
    }
    setStockSaving(true);
    try {
      const { data } = await api.post("/products/stock-adjust", {
        product_id: stockProduct.id,
        outlet_id: stockProduct.outlet_id,
        delta,
        reason: stockForm.reason.trim(),
      });
      toast.success(`Stok berhasil disesuaikan: ${data.new_stock} ${stockProduct.unit || "pcs"}`);
      setStockOpen(false);
      const [productsRes, balancesRes] = await Promise.all([
        api.get("/products"),
        api.get("/inventory/location-balances"),
      ]);
      setProducts(productsRes.data || []);
      setBalances(balancesRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyesuaikan stok");
    } finally {
      setStockSaving(false);
    }
  }


  useEffect(() => {
    let alive = true;

    Promise.all([
      api.get("/products"),
      api.get("/ingredients"),
      api.get("/outlets"),
      api.get("/warehouses"),
      api.get("/inventory/location-balances"),
    ]).then(([productsRes, ingredientsRes, outletsRes, warehousesRes, balancesRes]) => {
      if (!alive) return;
      setProducts(productsRes.data || []);
      setIngredients(ingredientsRes.data || []);
      setOutlets(outletsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setBalances(balancesRes.data || []);
      setInventoryError("");
    }).catch((error) => {
      if (!alive) return;
      setInventoryError(
        error?.response?.status === 403
          ? "Akses saldo inventory ditolak. Periksa permission role pengguna."
          : "Gagal memuat inventory. Periksa koneksi API dan coba muat ulang."
      );
    });

    return () => { alive = false; };
  }, []);

  const outletName = (id) => outlets.find(o=>o.id===id)?.name || "-";
  const activeCentralWarehouses = warehouses.filter(w => w.active !== false && w.kind === "central");
  const centralWarehouse = activeCentralWarehouses[0] || null;
  const warehouseName = (id) => warehouses.find(w => w.id === id)?.name || "-";
  const locationStock = (entity, entityId, locationType, locationId) => {
    const row = balances.find(b =>
      b.entity === entity &&
      b.entity_id === entityId &&
      b.location_type === locationType &&
      b.location_id === locationId
    );
    return Number(row?.qty ?? 0);
  };

  const ingredientLocations = (ingredientId) => [
    ...warehouses.filter(w => w.active !== false).map(w => ({
      id: w.id,
      name: w.name,
      type: "warehouse",
      label: "Gudang",
      qty: locationStock("ingredient", ingredientId, "warehouse", w.id),
    })),
    ...outlets.filter(o => o.active !== false).map(o => ({
      id: o.id,
      name: o.name,
      type: "outlet",
      label: "Outlet",
      qty: locationStock("ingredient", ingredientId, "outlet", o.id),
    })),
  ];
  const ingredientStock = (ingredientId) => balances
    .filter(b => b.entity === "ingredient" && b.entity_id === ingredientId &&
      ["warehouse", "outlet"].includes(b.location_type))
    .reduce((total, b) => total + Number(b.qty || 0), 0);

  const ingredientLocationOptions = [
    { value: "all", label: "Semua Lokasi" },
    { value: "warehouses", label: "Semua Gudang" },
    { value: "outlets", label: "Semua Outlet" },
    ...warehouses.map(w => ({ value: `warehouse:${w.id}`, label: `Gudang — ${w.name}` })),
    ...outlets.map(o => ({ value: `outlet:${o.id}`, label: `Outlet — ${o.name}` })),
  ];





  const filteredIngredients = ingredients.filter(i => {
    const q = ingredientSearch.trim().toLowerCase();
    const matchesSearch = !q || [i.name, i.unit].some(v => String(v ?? "").toLowerCase().includes(q));
    const stock = ingredientStock(i.id);
    const critical = stock <= Number(i.min_stock ?? 5);
    const matchesStatus = ingredientStatusFilter === "all" ||
      (ingredientStatusFilter === "critical" ? critical : !critical);
    return matchesSearch && matchesStatus;
  });

  const filteredIngredientRows = inventorySorted(
    filteredIngredients, ingredientSort,
    (ingredient, key) => key === "stock" ? ingredientStock(ingredient.id) : ingredient[key]
  ).flatMap(ingredient => {
    const locations = ingredientLocations(ingredient.id);
    const visible = locations.filter(loc => {
      if (ingredientLocationFilter === "all") return true;
      if (ingredientLocationFilter === "warehouses") return loc.type === "warehouse";
      if (ingredientLocationFilter === "outlets") return loc.type === "outlet";
      return `${loc.type}:${loc.id}` === ingredientLocationFilter;
    });
    return visible.map(loc => ({
      ingredient, loc, stock: loc.qty,
      critical: loc.qty <= Number(ingredient.min_stock ?? 5),
    }));
  });

  const filteredProducts = products.filter(p => {
    const q = productSearch.trim().toLowerCase();
    const matchesSearch = !q || [p.name, p.sku, p.brand].some(v => String(v ?? "").toLowerCase().includes(q));
    const matchesOutlet = productOutletFilter === "all" || p.outlet_id === productOutletFilter;
    const stock = locationStock("product", p.id, "outlet", p.outlet_id);
    const critical = stock <= Number(p.min_stock || 0);
    const matchesStatus = productStatusFilter === "all" || (productStatusFilter === "critical" ? critical : !critical);
    return matchesSearch && matchesOutlet && matchesStatus;
  });



  return (
    <div className="page-shell">
      <div data-workspace-redundant-header><div>
        <h1 className="text-2xl font-extrabold font-display">Inventory</h1></div>
        <p className="text-slate-500 text-sm">Kelola stok produk jadi & bahan baku</p>
        {inventoryError && (
          <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {inventoryError}
          </div>
        )}
      </div>
      <Tabs value={activeTab} onValueChange={changeInventoryTab}>
        <TabsContent value="products">
          <Card>
            <CardHeader><CardTitle className="text-base">Stok Produk Jadi</CardTitle></CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center"><Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Cari nama produk, SKU, atau brand..." className="sm:max-w-sm" /><Select value={productOutletFilter} onValueChange={setProductOutletFilter}><SelectTrigger className="sm:w-56"><SelectValue placeholder="Semua outlet" /></SelectTrigger><SelectContent><SelectItem value="all">Semua outlet</SelectItem>{outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select><Select value={productStatusFilter} onValueChange={setProductStatusFilter}><SelectTrigger className="sm:w-44"><SelectValue placeholder="Semua status" /></SelectTrigger><SelectContent><SelectItem value="all">Semua status</SelectItem><SelectItem value="critical">Kritis</SelectItem><SelectItem value="safe">Aman</SelectItem></SelectContent></Select></div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead><button type="button" className="inline-flex items-center gap-1 font-semibold hover:text-orange-600" onClick={() => setProductSort(c => ({key:"name",type:"text",direction:c.key==="name" && c.direction==="asc"?"desc":"asc"}))}>Produk <span>{productSort.key==="name"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead><TableHead><button type="button" onClick={() => toggleProductSort("outlet")}>Outlet <span>{productSort.key==="outlet"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead><TableHead><button type="button" onClick={() => toggleProductSort("brand")}>Brand <span>{productSort.key==="brand"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead className="text-right"><button type="button" onClick={() => toggleProductSort("stock", "number")}>Stok <span>{productSort.key==="stock"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead><TableHead className="text-right"><button type="button" onClick={() => toggleProductSort("min_stock", "number")}>Min <span>{productSort.key==="min_stock"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead className="text-right"><button type="button" onClick={() => toggleProductSort("hpp", "number")}>HPP <span>{productSort.key==="hpp"?(productSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead><TableHead>Status</TableHead><TableHead>Qty Order</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {inventorySorted(filteredProducts, productSort, (p, key) => { if (key === "outlet") return outletName(p.outlet_id); if (key === "stock") return locationStock("product", p.id, "outlet", p.outlet_id); return p[key]; }).map(p => {
                    const stock = locationStock("product", p.id, "outlet", p.outlet_id);
                    const critical = stock <= Number(p.min_stock || 0);
                    return (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="text-xs">{outletName(p.outlet_id)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{p.brand}</Badge></TableCell>
                        <TableCell className="text-right num font-semibold">{stock} {p.unit}</TableCell>
                        <TableCell className="text-right num text-slate-500">{p.min_stock}</TableCell>
                        <TableCell className="text-right num text-slate-500">{rupiah(p.hpp)}</TableCell>
                        <TableCell>{critical ? <Badge variant="destructive">Kritis</Badge> : <Badge className="bg-emerald-500">Aman</Badge>}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <div className="flex gap-1 items-center"><Input className="w-20" aria-label={`Qty order ${p.name}`} type="number" min="0.01" step="any" value={orderQty[`product:${p.id}`] ?? "0"} onChange={e => setOrderQty(q => ({...q, [`product:${p.id}`]: e.target.value}))}/><Button type="button" size="sm" onClick={() => addToOrderCart("product", p, "outlet", p.outlet_id, outletName(p.outlet_id))}>Order</Button></div>
                            <div className="flex gap-1 items-center"><Input className="w-20" aria-label={`Qty Transfer ${p.name}`} type="number" min="0" step="any" value={transferQty[`product:${p.id}:outlet:${p.outlet_id}`] ?? "0"} onChange={e => setTransferQty(q => ({...q, [`product:${p.id}:outlet:${p.outlet_id}`]: e.target.value}))}/><Button type="button" size="sm" variant="outline" onClick={() => addToTransferCart("product", p, "outlet", p.outlet_id, outletName(p.outlet_id))}>Transfer</Button></div>
                            <Button type="button" size="sm" variant="outline" onClick={() => {
                              setMinStockProduct(p);
                              setMinStockValue(String(p.min_stock ?? 0));
                              setStockProduct(p);
                              setStockForm({ delta: "", reason: "" });
                              setMinStockOpen(true);
                            }}><Pencil className="mr-1 h-3 w-3" />Edit</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="ingredients">
          <Card>
            <CardHeader><CardTitle className="text-base">Stok Bahan Baku (Semua Lokasi)</CardTitle></CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input value={ingredientSearch} onChange={e => setIngredientSearch(e.target.value)} placeholder="Cari nama bahan atau unit..." className="sm:max-w-sm" />
                <Select value={ingredientStatusFilter} onValueChange={setIngredientStatusFilter}>
                  <SelectTrigger className="sm:w-44"><SelectValue placeholder="Semua status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua status</SelectItem>
                    <SelectItem value="critical">Kritis</SelectItem>
                    <SelectItem value="safe">Aman</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ingredientLocationFilter} onValueChange={setIngredientLocationFilter}>
                  <SelectTrigger className="sm:w-60"><SelectValue placeholder="Semua Lokasi" /></SelectTrigger>
                  <SelectContent>
                    {ingredientLocationOptions.map(loc => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!centralWarehouse && <div role="alert" className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Gudang pusat aktif belum ditemukan. Aktifkan atau buat gudang dengan tipe central sebelum menyesuaikan stok.</div>}
              <Table>
                <TableHeader><TableRow>
                  <TableHead><button type="button" className="inline-flex items-center gap-1 font-semibold hover:text-orange-600" onClick={() => toggleIngredientSort("name")}>Bahan <span>{ingredientSort.key==="name"?(ingredientSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead>Lokasi</TableHead>
                  <TableHead><button type="button" onClick={() => toggleIngredientSort("unit")}>Unit <span>{ingredientSort.key==="unit"?(ingredientSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead className="text-right"><button type="button" onClick={() => toggleIngredientSort("stock", "number")}>Stok <span>{ingredientSort.key==="stock"?(ingredientSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead className="text-right"><button type="button" onClick={() => toggleIngredientSort("min_stock", "number")}>Min <span>{ingredientSort.key==="min_stock"?(ingredientSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead className="text-right"><button type="button" onClick={() => toggleIngredientSort("cost", "number")}>Harga/unit <span>{ingredientSort.key==="cost"?(ingredientSort.direction==="asc"?"↑":"↓"):"↕"}</span></button></TableHead>
                  <TableHead>Status</TableHead><TableHead>Qty Order</TableHead><TableHead>Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredIngredientRows.map(({ingredient: i, loc, stock, critical}) => (
                    <TableRow key={`${i.id}-${loc.type}-${loc.id}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="text-xs">{loc.label}: {loc.name}</TableCell>
                      <TableCell className="text-xs">{i.unit}</TableCell>
                      <TableCell className="text-right num font-semibold">
                        <button type="button" className="underline decoration-dotted underline-offset-4 hover:text-orange-600" onClick={() => { setIngredientLocationsItem(i); setIngredientLocationsOpen(true); }} title="Lihat rincian stok">{stock.toFixed(2)}</button>
                      </TableCell>
                      <TableCell className="text-right num text-slate-500">{i.min_stock ?? 5}</TableCell>
                      <TableCell className="text-right num text-slate-500">{rupiah(i.cost)}</TableCell>
                      <TableCell>{critical ? <Badge variant="destructive">Kritis</Badge> : <Badge className="bg-emerald-500">Aman</Badge>}</TableCell>
                      <TableCell><div className="flex flex-wrap gap-2">
                        <div className="flex gap-1 items-center"><Input className="w-20" aria-label={`Qty order ${i.name}`} type="number" min="0.01" step="any" value={orderQty[`ingredient:${i.id}`] ?? "0"} onChange={e => setOrderQty(q => ({...q, [`ingredient:${i.id}`]: e.target.value}))}/><Button type="button" size="sm" onClick={() => addToOrderCart("ingredient", i, loc.type, loc.id, `${loc.label}: ${loc.name}`)}>Order</Button></div>
                        <div className="flex gap-1 items-center"><Input className="w-20" aria-label={`Qty Transfer ${i.name}`} type="number" min="0" step="any" value={transferQty[`ingredient:${i.id}:${loc.type}:${loc.id}`] ?? "0"} onChange={e => setTransferQty(q => ({...q, [`ingredient:${i.id}:${loc.type}:${loc.id}`]: e.target.value}))}/><Button type="button" size="sm" variant="outline" onClick={() => addToTransferCart("ingredient", i, loc.type, loc.id, `${loc.label}: ${loc.name}`)}>Transfer</Button></div>
                        <Button type="button" size="sm" variant="outline" onClick={() => openIngredientMin(i)}><Pencil className="mr-1 h-3 w-3" />Edit</Button>

                      </div></TableCell>
                    </TableRow>
                  ))}
                  {filteredIngredientRows.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-slate-500">Tidak ada stok bahan baku pada lokasi dan filter ini.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="opname"><OpnameTab outlets={outlets} warehouses={warehouses} /></TabsContent>
        <TabsContent value="stock-ledger">
          <Card>
            <CardHeader><CardTitle className="text-base">Stock Ledger</CardTitle></CardHeader>
            <CardContent>
              <div className="py-12 text-center">
                <p className="font-medium text-slate-600">Stock Ledger</p>
                <p className="mt-2 text-sm text-slate-500">Modul Stock Ledger akan dikembangkan pada tahap berikutnya.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      {orderCart.length > 0 && <Card className="mt-4">
        <CardHeader><CardTitle className="flex flex-wrap justify-between gap-2 text-base">Daftar Pesanan Sementara ({orderCart.length} item) <span>{rupiah(orderTotal)}</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {orderCart.map(item => <div key={item.key} className="grid grid-cols-[1fr_90px_auto_auto] gap-2 items-center border-b pb-2">
            <div>
              <p className="font-medium">{item.name}</p>
              <p className="text-xs text-slate-500">{item.entity === "ingredient" ? "Bahan baku" : "Produk"} · {rupiah(item.unit_price)}/{item.unit}</p>
              <Select
                value={`${item.location_type}:${item.location_id}`}
                onValueChange={value => {
                  const [location_type, location_id] = value.split(":");
                  const options = location_type === "warehouse" ? warehouses : outlets;
                  const selected = options.find(x => x.id === location_id);
                  setOrderCart(old => old.map(x => x.key === item.key
                    ? {...x, location_type, location_id, location_name: selected?.name || location_id}
                    : x));
                }}
              >
                <SelectTrigger className="mt-2 h-8 w-full text-xs">
                  <SelectValue placeholder="Pilih lokasi tujuan" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={`warehouse:${w.id}`} value={`warehouse:${w.id}`}>Gudang · {w.name}</SelectItem>)}
                  {outlets.map(o => <SelectItem key={`outlet:${o.id}`} value={`outlet:${o.id}`}>Outlet · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input aria-label={`Qty keranjang ${item.name}`} type="number" min="0.01" step="any" value={item.qty} onChange={e => {const v=e.target.value;setOrderCart(old=>old.map(x=>x.key===item.key?{...x,qty:v===""?"":Number(v),subtotal:v===""?0:Number(v)*x.unit_price}:x));}}/>
            <span className="text-sm font-semibold">{rupiah(item.subtotal)}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setOrderCart(old => old.filter(x => x.key !== item.key))}>Hapus</Button>
          </div>)}
          <div className="flex flex-wrap justify-between gap-2"><Button type="button" variant="outline" onClick={() => setOrderCart([])}>Kosongkan Daftar</Button><Button type="button" className="bg-orange-500 hover:bg-orange-600" onClick={sendOrderToPurchasing}>Buat Purchase Order</Button></div>
          <p className="text-xs text-slate-500">Daftar sementara; belum membuat PO dan belum mengubah stok.</p>
        </CardContent>
      </Card>}
      {transferCart.length > 0 && <Card className="mt-4 border-orange-200">
        <CardHeader>
          <CardTitle className="flex flex-wrap justify-between gap-2 text-base">
            Keranjang Transfer ({transferCart.length} item)
            <span className="text-sm font-normal">{transferCart[0].location_name}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {transferCart.map(item => <div key={item.key} className="grid grid-cols-[1fr_100px_auto] items-center gap-2 border-b pb-2">
            <div>
              <p className="font-medium">{item.name}</p>
              <p className="text-xs text-slate-500">
                {item.entity === "ingredient" ? "Bahan baku" : "Produk"} · {item.unit}
              </p>
            </div>
            <Input
              aria-label={`Qty Transfer ${item.name}`}
              type="number"
              min="0.01"
              step="any"
              value={item.qty}
              onChange={e => {
                const value = e.target.value;
                setTransferCart(old => old.map(x => x.key === item.key
                  ? {...x, qty: value === "" ? "" : Number(value)}
                  : x
                ));
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setTransferCart(old => old.filter(x => x.key !== item.key))}
            >Hapus</Button>
          </div>)}
          <div className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" onClick={() => setTransferCart([])}>
              Kosongkan Keranjang
            </Button>
            <Button
              type="button"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={() => {
                const items = transferCart.filter(x =>
                  Number.isFinite(Number(x.qty)) && Number(x.qty) > 0
                );
                if (!items.length || items.length !== transferCart.length) {
                  toast.error("Periksa kembali jumlah semua barang Transfer");
                  return;
                }
                navigate("/stock-out", {
                  state: {
                    inventoryTransferItems: items.map(({key, ...item}) => ({
                      ...item,
                      qty: Number(item.qty)
                    }))
                  }
                });
              }}
            >Submit</Button>
          </div>
          <p className="text-xs text-slate-500">
            Daftar sementara; stok belum berubah. Transfer akan mengikuti alur approval, dispatch, dan penerimaan yang sudah ada.
          </p>
        </CardContent>
      </Card>}

      <Dialog open={ingredientLocationsOpen} onOpenChange={setIngredientLocationsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rincian Stok per Lokasi</DialogTitle></DialogHeader>
          {ingredientLocationsItem && <>
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <p className="font-semibold">{ingredientLocationsItem.name}</p>
              <p className="text-slate-600">Total saldo tercatat: {ingredientStock(ingredientLocationsItem.id).toFixed(2)} {ingredientLocationsItem.unit || ""}</p>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Jenis</TableHead><TableHead>Lokasi</TableHead><TableHead className="text-right">Stok</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {ingredientLocations(ingredientLocationsItem.id).map(loc => (
                  <TableRow key={`${loc.type}-${loc.id}`}>
                    <TableCell>{loc.label}</TableCell>
                    <TableCell>{loc.name}</TableCell>
                    <TableCell className="text-right num">{loc.qty.toFixed(2)} {ingredientLocationsItem.unit || ""}</TableCell>
                  </TableRow>
                ))}
                {ingredientLocations(ingredientLocationsItem.id).length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500">Belum ada lokasi aktif.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIngredientLocationsOpen(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ingredientMinOpen} onOpenChange={setIngredientMinOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Bahan Baku</DialogTitle></DialogHeader>
          {ingredientMinItem && ingredientStockItem && <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <p className="font-semibold">{ingredientMinItem.name}</p>
              <p className="text-slate-600">Total stok semua lokasi: {ingredientStock(ingredientMinItem.id).toFixed(2)} {ingredientMinItem.unit || ""}</p>
            </div>
            <form onSubmit={saveIngredientMin} className="space-y-2">
              <label className="text-sm font-medium">Batas stok minimum *</label>
              <Input type="number" min="0" step="any" value={ingredientMinValue} onChange={e => setIngredientMinValue(e.target.value)} required />
              <Button type="submit" size="sm" variant="outline" disabled={ingredientMinSaving}>
                {ingredientMinSaving ? "Menyimpan..." : "Simpan Stok Minimum"}
              </Button>
            </form>
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold">Penyesuaian stok</p>
              <label className="text-sm font-medium">Jenis lokasi *</label>
              <Select value={ingredientStockForm.location_type} onValueChange={v => setIngredientStockForm(s => ({
                ...s, location_type: v,
                location_id: v === "warehouse"
                  ? (activeCentralWarehouses[0]?.id || warehouses.find(w => w.active !== false)?.id || "")
                  : (outlets.find(o => o.active !== false)?.id || ""),
              }))}>
                <SelectTrigger><SelectValue placeholder="Pilih jenis lokasi" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">Gudang</SelectItem>
                  <SelectItem value="outlet">Outlet</SelectItem>
                </SelectContent>
              </Select>
              <label className="text-sm font-medium">Lokasi *</label>
              <Select value={ingredientStockForm.location_id} onValueChange={v => setIngredientStockForm(s => ({...s, location_id:v}))}>
                <SelectTrigger><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
                <SelectContent>
                  {(ingredientStockForm.location_type === "outlet"
                    ? outlets.filter(o => o.active !== false)
                    : warehouses.filter(w => w.active !== false)
                  ).map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {ingredientStockForm.location_id && <p className="text-xs text-slate-500">Saldo lokasi ini: {locationStock("ingredient", ingredientStockItem.id, ingredientStockForm.location_type, ingredientStockForm.location_id).toFixed(2)} {ingredientStockItem.unit || ""}</p>}
              <label className="text-sm font-medium">Perubahan stok (+ tambah, - kurangi) *</label>
              <Input type="number" step="any" value={ingredientStockForm.delta} onChange={e => setIngredientStockForm(s => ({...s, delta:e.target.value}))} placeholder="Contoh: 10 atau -2" />
              <label className="text-sm font-medium">Alasan penyesuaian *</label>
              <Input value={ingredientStockForm.reason} onChange={e => setIngredientStockForm(s => ({...s, reason:e.target.value}))} placeholder="Contoh: hasil stock opname" />
              <Button type="button" size="sm" disabled={ingredientStockSaving} onClick={() => saveIngredientStock({preventDefault: () => {}})}>
                {ingredientStockSaving ? "Menyimpan..." : "Simpan Penyesuaian Stok"}
              </Button>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIngredientMinOpen(false)} disabled={ingredientMinSaving || ingredientStockSaving}>Tutup</Button>
            </DialogFooter>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={minStockOpen} onOpenChange={setMinStockOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Produk</DialogTitle></DialogHeader>
          {minStockProduct && stockProduct && <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <p className="font-semibold">{minStockProduct.name}</p>
              <p className="text-slate-600">Outlet: {outletName(minStockProduct.outlet_id)}</p>
              <p className="text-slate-600">Stok saat ini: {locationStock("product", minStockProduct.id, "outlet", minStockProduct.outlet_id)} {minStockProduct.unit || "pcs"}</p>
            </div>
            <form onSubmit={saveMinStock} className="space-y-2">
              <label className="text-sm font-medium">Batas stok minimum *</label>
              <Input type="number" min="0" step="any" value={minStockValue} onChange={e => setMinStockValue(e.target.value)} required />
              <Button type="submit" size="sm" variant="outline" disabled={minStockSaving}>
                {minStockSaving ? "Menyimpan..." : "Simpan Stok Minimum"}
              </Button>
            </form>
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold">Penyesuaian stok</p>
              <label className="text-sm font-medium">Perubahan stok (+ tambah, - kurangi) *</label>
              <Input type="number" step="any" value={stockForm.delta} onChange={e => setStockForm(v => ({...v, delta:e.target.value}))} placeholder="Contoh: 10 atau -2" />
              <label className="text-sm font-medium">Alasan penyesuaian *</label>
              <Input value={stockForm.reason} onChange={e => setStockForm(v => ({...v, reason:e.target.value}))} placeholder="Contoh: hasil stock opname" />
              <Button type="button" size="sm" disabled={stockSaving} onClick={() => saveStock({preventDefault: () => {}})}>
                {stockSaving ? "Menyimpan..." : "Simpan Penyesuaian Stok"}
              </Button>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMinStockOpen(false)} disabled={minStockSaving || stockSaving}>Tutup</Button>
            </DialogFooter>
          </div>}
        </DialogContent>
      </Dialog>
      </Tabs>
    </div>
  );
}

function OpnameTab({ outlets = [], warehouses = [] }) {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [counts, setCounts] = useState({});
  const [scope, setScope] = useState("ingredients");
  const [locationKey, setLocationKey] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDate, setHistoryDate] = useState("");

  const load = () => api.get("/opname/sessions").then(({data})=>setSessions(data));
  useEffect(() => { load(); }, []);

  async function createSession() {
    if (!locationKey) {
      toast.error("Pilih lokasi Stock Opname terlebih dahulu");
      return;
    }

    const [locationType, locationId] = locationKey.split(":");
    if (!locationId || !["warehouse", "outlet"].includes(locationType)) {
      toast.error("Lokasi Stock Opname tidak valid");
      return;
    }

    const payload = {
      scope,
      warehouse_id: locationType === "warehouse" ? locationId : null,
      outlet_id: locationType === "outlet" ? locationId : null,
    };

    try {
      const { data } = await api.post("/opname/sessions", payload);
      toast.success(`Session dibuat: ${data.expected.length} item`);
      setActive(data);
      setCounts({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  }
  async function openSession(s) {
    const { data } = await api.get(`/opname/sessions/${s.id}`);
    setActive(data);
    const c = {};
    (data.variance || data.expected || []).forEach(e => { c[e.entity_id] = e.counted_qty ?? e.expected_qty; });
    setCounts(c);
  }
  async function submit() {
    if (!active) return;
    const lines = active.expected.map(e => ({ entity_id: e.entity_id, counted_qty: Number(counts[e.entity_id] ?? e.expected_qty), reason: "" }));
    try {
      const { data } = await api.post(`/opname/sessions/${active.id}/submit`, { lines });
      toast.success("Submit ✓"); setActive(data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  async function postSession() {
    if (!active) return;
    try {
      const { data } = await api.post(`/opname/sessions/${active.id}/post`);
      toast.success(`Posted ${data.posted_lines || 0} adjustment`); setActive(data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Sessions</div>
        </div>
        <div className="space-y-2 mb-3">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="opname-scope-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="ingredients">Bahan Baku</SelectItem>
              <SelectItem value="products">Produk</SelectItem>
            </SelectContent>
          </Select>
          <Select value={locationKey} onValueChange={setLocationKey}>
            <SelectTrigger data-testid="opname-location-select">
              <SelectValue placeholder="Pilih gudang atau outlet" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.filter(w => w.active !== false).map(w => (
                <SelectItem key={`warehouse:${w.id}`} value={`warehouse:${w.id}`}>
                  Gudang — {w.name}
                </SelectItem>
              ))}
              {outlets.filter(o => o.active !== false).map(o => (
                <SelectItem key={`outlet:${o.id}`} value={`outlet:${o.id}`}>
                  Outlet — {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="opname-new-btn" onClick={createSession} disabled={!locationKey} className="w-full bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Session Baru</Button>
        </div>
        {(() => {
          const activeSessions = sessions.filter(s => s.status === "open");
          const pendingSessions = sessions.filter(s => s.status === "submitted");
          const postedSessions = sessions.filter(s => s.status === "posted" &&
            (!historyDate || String(s.created_at || "").slice(0, 10) === historyDate));

          const renderSession = s => (
            <button data-testid={`opname-session-${s.id}`} key={s.id} onClick={() => openSession(s)}
              className={`w-full text-left p-2 rounded text-xs border ${active?.id === s.id ? "border-orange-500 bg-orange-50" : "hover:bg-slate-50"}`}>
              <div className="flex justify-between">
                <span className="font-mono">{s.id.slice(0, 8)}</span>
                <Badge className={s.status === "posted" ? "bg-emerald-500" : s.status === "submitted" ? "bg-blue-500" : "bg-amber-500"}>{s.status}</Badge>
              </div>
              <div className="text-slate-500">
                {s.scope === "ingredients" ? "Bahan Baku" : "Produk"} ·{" "}
                {s.location_type === "warehouse"
                  ? `Gudang — ${warehouses.find(w => w.id === s.location_id)?.name || s.location_id || s.warehouse_id || "-"}`
                  : `Outlet — ${outlets.find(o => o.id === s.location_id)?.name || s.location_id || s.outlet_id || "-"}`}
              </div>
              <div className="text-xs text-slate-400">{(s.expected || []).length} item · {s.created_at?.slice(0, 16)}</div>
            </button>
          );

          return (
            <div className="space-y-4">
              <section>
                <div className="font-medium text-sm mb-2">Sesi Aktif ({activeSessions.length})</div>
                <div className="space-y-1 max-h-[300px] overflow-auto">
                  {activeSessions.map(renderSession)}
                  {!activeSessions.length && <div className="text-xs text-slate-400 py-3 text-center">Tidak ada sesi aktif</div>}
                </div>
              </section>

              {pendingSessions.length > 0 && (
                <section>
                  <div className="font-medium text-sm mb-2">Menunggu Posting ({pendingSessions.length})</div>
                  <div className="space-y-1 max-h-[250px] overflow-auto">{pendingSessions.map(renderSession)}</div>
                </section>
              )}

              <section className="border-t pt-3">
                <button type="button" onClick={() => setHistoryOpen(v => !v)}
                  className="w-full flex items-center justify-between text-sm font-medium py-1"
                  aria-expanded={historyOpen}>
                  <span>Riwayat Stock Opname ({sessions.filter(s => s.status === "posted").length})</span>
                  <span>{historyOpen ? "Tutup ▲" : "Tampilkan ▼"}</span>
                </button>
                {historyOpen && (
                  <div className="space-y-2 mt-2">
                    <label className="block text-xs text-slate-500" htmlFor="opname-history-date">Filter tanggal sesi</label>
                    <div className="flex items-center gap-2">
                      <input id="opname-history-date" type="date" value={historyDate}
                        onChange={e => setHistoryDate(e.target.value)}
                        className="border rounded px-2 py-1 text-sm min-w-0 flex-1" />
                      {historyDate && <button type="button" onClick={() => setHistoryDate("")} className="text-xs underline">Hapus filter</button>}
                    </div>
                    <div className="space-y-1 max-h-[300px] overflow-auto">
                      {postedSessions.map(renderSession)}
                      {!postedSessions.length && <div className="text-xs text-slate-400 py-3 text-center">Tidak ada riwayat pada tanggal tersebut</div>}
                    </div>
                  </div>
                )}
              </section>
            </div>
          );
        })()}
      </Card>
      <Card className="p-4">
        {!active ? <div className="text-center text-slate-400 py-16">Pilih atau buat session baru</div> : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-bold">Session {active.id.slice(0,8)}</div>
                <div className="text-xs text-slate-500">
                  {active.scope === "ingredients" ? "Bahan Baku" : "Produk"} ·{" "}
                  {active.location_type === "warehouse"
                    ? `Gudang — ${warehouses.find(w => w.id === active.location_id)?.name || active.location_id || active.warehouse_id || "-"}`
                    : `Outlet — ${outlets.find(o => o.id === active.location_id)?.name || active.location_id || active.outlet_id || "-"}`
                  } · Status: <Badge className={active.status==="posted"?"bg-emerald-500":active.status==="submitted"?"bg-blue-500":"bg-amber-500"}>{active.status}</Badge>
                </div>
              </div>
              <div className="space-x-2">
                {active.status==="open" && <Button data-testid="opname-submit-btn" onClick={submit} className="bg-blue-500 hover:bg-blue-600">Submit</Button>}
                {active.status==="submitted" && <Button data-testid="opname-post-btn" onClick={postSession} className="bg-emerald-500 hover:bg-emerald-600">Post ke Ledger</Button>}
              </div>
            </div>
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b sticky top-0 bg-white">
                  <tr><th className="text-left py-2">Item</th><th className="text-right">Sistem</th><th className="text-right">Hitung Fisik</th><th className="text-right">Variance</th></tr>
                </thead>
                <tbody>
                  {(active.variance || active.expected || []).map(e => {
                    const counted = counts[e.entity_id] ?? e.counted_qty ?? e.expected_qty;
                    const variance = Number(counted) - Number(e.expected_qty);
                    return (
                      <tr key={e.entity_id} className="border-b">
                        <td className="py-1">{e.name}</td>
                        <td className="text-right num">{Number(e.expected_qty).toFixed(2)}</td>
                        <td className="text-right">
                          {active.status==="open"
                            ? <Input type="number" value={counts[e.entity_id] ?? e.expected_qty} onChange={ev=>setCounts({...counts,[e.entity_id]:ev.target.value})} className="h-8 w-24 num text-right ml-auto"/>
                            : <span className="num">{Number(counted).toFixed(2)}</span>}
                        </td>
                        <td className={`text-right num font-bold ${variance>0?"text-emerald-600":variance<0?"text-rose-600":"text-slate-400"}`}>{variance.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function inventoryCompare(a, b, type = "text") {
  if (type === "number") return (Number(a ?? 0) || 0) - (Number(b ?? 0) || 0);
  return String(a ?? "").localeCompare(String(b ?? ""), "id", { numeric: true, sensitivity: "base" });
}
function inventorySorted(rows, config, getValue) {
  return [...(rows || [])].sort((a, b) => {
    const result = inventoryCompare(getValue(a, config.key), getValue(b, config.key), config.type);
    return config.direction === "asc" ? result : -result;
  });
}
